// eSAAR finite-state machine — canonical transitions for the 15-state workflow.
// Source: docs/dha-strategic-portfolio-nexus/.../ESAAR_ACCESS_RBAC_AND_PROFILE_WORKFLOW.md + CORRECTION_MEMO_V2.md

import { WORKFLOW_STATES, ACCESS_REQUEST_ACTIONS } from './enums.js';

const STATES = new Set(WORKFLOW_STATES);
const ACTIONS = new Set(ACCESS_REQUEST_ACTIONS);

// Transitions map: state -> action -> { next, requiredRoles: string[] }
// requiredRoles: any one of the listed role IDs is sufficient (OR semantics). system_admin always passes.
export const TRANSITIONS = Object.freeze({
  draft: {
    submit: { next: 'submitted', requiredRoles: ['*self*'] },
    withdraw: { next: 'withdrawn', requiredRoles: ['*self*'] },
  },
  submitted: {
    approve: { next: 'supervisor_review', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
    requestRework: { next: 'returned_for_rework', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
    deny: { next: 'denied', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
  },
  supervisor_review: {
    approve: { next: 'security_review', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
    requestRework: { next: 'returned_for_rework', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
    deny: { next: 'denied', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
  },
  security_review: {
    approve: {
      next: 'portfolio_owner_review',
      requiredRoles: ['ROLE-SECURITY-COMPLIANCE-REVIEWER'],
    },
    requestRework: {
      next: 'returned_for_rework',
      requiredRoles: ['ROLE-SECURITY-COMPLIANCE-REVIEWER'],
    },
    deny: { next: 'denied', requiredRoles: ['ROLE-SECURITY-COMPLIANCE-REVIEWER'] },
  },
  portfolio_owner_review: {
    approve: {
      next: 'data_steward_review',
      requiredRoles: ['ROLE-PORTFOLIO-MANAGER', 'ROLE-EXECUTIVE-SPONSOR'],
    },
    requestRework: { next: 'returned_for_rework', requiredRoles: ['ROLE-PORTFOLIO-MANAGER'] },
    deny: { next: 'denied', requiredRoles: ['ROLE-PORTFOLIO-MANAGER'] },
  },
  data_steward_review: {
    approve: { next: 'admin_provisioning', requiredRoles: ['ROLE-DATA-STEWARD'] },
    requestRework: { next: 'returned_for_rework', requiredRoles: ['ROLE-DATA-STEWARD'] },
    deny: { next: 'denied', requiredRoles: ['ROLE-DATA-STEWARD'] },
  },
  admin_provisioning: {
    approve: { next: 'provisioned', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
    deny: { next: 'denied', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
  },
  provisioned: {
    approve: { next: 'active', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
    revoke: { next: 'revoked', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
  },
  active: {
    revoke: { next: 'revoked', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
    recertify: { next: 'recertification_due', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
  },
  returned_for_rework: {
    submit: { next: 'submitted', requiredRoles: ['*self*'] },
    withdraw: { next: 'withdrawn', requiredRoles: ['*self*'] },
  },
  recertification_due: {
    approve: { next: 'active', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
    deny: { next: 'expired', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
    revoke: { next: 'revoked', requiredRoles: ['ROLE-SYSTEM-ADMIN'] },
  },
  denied: {},
  withdrawn: {},
  expired: {},
  revoked: {},
});

export function isTerminalState(state) {
  return ['denied', 'withdrawn', 'expired', 'revoked'].includes(state);
}

export function isValidState(state) {
  return STATES.has(state);
}

export function isValidAction(action) {
  return ACTIONS.has(action);
}

export function allowedActions(currentState) {
  return Object.keys(TRANSITIONS[currentState] || {});
}

/**
 * Resolve a transition. Returns { ok: true, nextState } or { ok: false, code, message }.
 *
 * @param {object} args
 * @param {string} args.currentState
 * @param {string} args.action          One of ACCESS_REQUEST_ACTIONS.
 * @param {string[]} args.actorRoles    Role IDs held by the acting user.
 * @param {boolean} args.actorIsRequester  True when acting user equals the request's requesterUserId.
 */
export function transition({ currentState, action, actorRoles = [], actorIsRequester = false }) {
  if (!isValidState(currentState)) {
    return { ok: false, code: 'VALIDATION_ERROR', message: `Unknown state: ${currentState}` };
  }
  if (!isValidAction(action)) {
    return { ok: false, code: 'VALIDATION_ERROR', message: `Unknown action: ${action}` };
  }
  const fromState = TRANSITIONS[currentState] || {};
  const t = fromState[action];
  if (!t) {
    return {
      ok: false,
      code: 'CONFLICT',
      message: `Action ${action} is not allowed from state ${currentState}.`,
    };
  }
  const roleSet = new Set(actorRoles);
  const allowed =
    roleSet.has('ROLE-SYSTEM-ADMIN') ||
    t.requiredRoles.some((r) => (r === '*self*' ? actorIsRequester : roleSet.has(r)));
  if (!allowed) {
    return {
      ok: false,
      code: 'ACCESS_DENIED',
      message: `Role(s) [${[...roleSet].join(',') || 'none'}] cannot perform ${action} from ${currentState}.`,
    };
  }
  return { ok: true, nextState: t.next };
}
